/**
 * ============================================================================
 * Web 端 HTTP 适配器
 * ============================================================================
 *
 * 文件职责：
 * - 将 Vben 的 requestClient 适配为符合 IRequestClient 接口的对象
 * - 供 shared 库的 API 工厂函数使用
 * - 实现依赖注入模式
 *
 * 设计理念：
 * - 适配器模式：将不同的请求客户端统一为相同接口
 * - 依赖注入：API 工厂函数通过适配器获得请求能力
 * - 平台无关：shared 库不需要知道具体使用的是 axios、fetch 还是其他库
 *
 * 使用方式：
 *
 * ```typescript
 * // 1. 导入适配器
 * import { httpAdapter } from '#/api/adapter/http'
 *
 * // 2. 传入工厂函数
 * import { createMaterialApi } from '@bayes-mes/shared/api/material/factory'
 * export const materialApi = createMaterialApi(httpAdapter)
 *
 * // 3. 使用生成的 API
 * const materials = await materialApi.getMaterialList({ page: 1 })
 * ```
 *
 * ⚠️ 关键说明：
 *
 * ### 1. 响应数据解包
 * Vben 的 requestClient 已经配置了 `responseReturn: 'data'`，
 * 这意味着它会自动解包后端返回的数据：
 *
 * ```typescript
 * // 后端返回：{ code: 0, data: { id: 1, name: 'xxx' }, message: 'success' }
 * // requestClient 返回：{ id: 1, name: 'xxx' }
 * ```
 *
 * 因此，适配器不需要额外的数据解包处理，直接转发即可。
 *
 * ### 2. 错误处理
 * requestClient 已经配置了完善的拦截器：
 * - 401 错误自动跳转登录
 * - 403 错误提示无权限
 * - 业务错误自动提示
 * - 网络错误自动提示
 *
 * 因此，适配器不需要额外的错误处理。
 *
 * ### 3. 类型安全
 * 适配器完整支持 TypeScript 泛型，确保类型推断正确：
 *
 * ```typescript
 * // API 工厂函数指定返回类型
 * client.get<Material>('/api/v1/materials/1')
 * // 返回：Promise<Material>
 * ```
 *
 * @module api/adapter/http
 * @description Web 端 HTTP 请求适配器
 *
 * @author AI Assistant
 * @created 2025-10-19
 * @last-updated 2025-10-19
 *
 * @change-log
 * - 2025-10-19: 初始创建，适配 Vben requestClient
 *
 * @notes
 * - 基于 Vben Admin 的 RequestClient
 * - 支持完整的拦截器功能
 * - 自动处理 Token、设备ID、语言等请求头
 * ============================================================================
 */

import type {
  IRequestClient,
  RequestConfig,
} from '@bayes-mes/shared/api/core/client';

import { REQUEST_TIMEOUT, batchClient, requestClient } from '#/api/request';

import { buildPatchRequestConfig } from './patch';

interface LogisticsCompatibleRequestClient extends IRequestClient {
  patch<T = any>(url: string, data?: any, config?: RequestConfig): Promise<T>;
}

const REPORT_TIMEOUT_URLS = ['/api/v1/production/wip/overview'];

function withUrlTimeout(
  url: string,
  config?: RequestConfig,
): RequestConfig | undefined {
  if (config?.timeout) {
    return config;
  }

  if (REPORT_TIMEOUT_URLS.some((reportUrl) => url.includes(reportUrl))) {
    return {
      ...config,
      timeout: REQUEST_TIMEOUT.REPORT,
    };
  }

  return config;
}

/**
 * Web 端 HTTP 适配器
 *
 * @description
 * 将 Vben 的 requestClient 适配为符合 IRequestClient 接口的对象。
 *
 * **核心特点**：
 * - 直接转发请求到 requestClient
 * - 无需额外的数据解包（已在 requestClient 配置）
 * - 无需额外的错误处理（已在拦截器中处理）
 * - 完整的类型安全和推断
 *
 * **与 requestClient 的关系**：
 * ```
 * API 工厂函数 → httpAdapter → Vben requestClient → 后端 API
 *     ↓              ↓               ↓
 *   类型安全      接口适配        实际请求
 * ```
 *
 * **为什么需要适配器？**
 * - shared 库是跨平台的，不能直接依赖 Vben 的 requestClient
 * - 通过适配器，实现了"依赖倒置"：shared 库依赖抽象接口，不依赖具体实现
 * - 如果将来要支持 App 端，只需创建 uniAdapter，无需修改 shared 库
 */
export const httpAdapter: LogisticsCompatibleRequestClient = {
  /**
   * GET 请求
   *
   * @description
   * 发送 GET 请求获取数据。
   *
   * **数据流向**：
   * ```
   * API 方法调用 → httpAdapter.get → requestClient.get → 后端 API
   *                                     ↓
   *                                拦截器处理
   *                                     ↓
   *                                自动解包数据
   *                                     ↓
   *                                返回实际数据
   * ```
   *
   * @template T 返回数据的类型
   * @param url 请求 URL（完整路径，如 /api/v1/materials）
   * @param config 请求配置（可选）
   * @returns Promise，resolve 时返回实际数据（已解包）
   *
   * @example
   * ```typescript
   * // 基础用法
   * const material = await httpAdapter.get<Material>('/api/v1/materials/1')
   *
   * // 带查询参数
   * const materials = await httpAdapter.get<PaginationResult<Material>>(
   *   '/api/v1/materials',
   *   { params: { page: 1, per_page: 20 } }
   * )
   * ```
   */
  get<T = any>(url: string, config?: RequestConfig): Promise<T> {
    return requestClient.get<T>(url, withUrlTimeout(url, config));
  },

  /**
   * POST 请求
   *
   * @description
   * 发送 POST 请求创建资源或执行操作。
   *
   * **数据流向**：
   * ```
   * API 方法调用 → httpAdapter.post → requestClient.post → 后端 API
   *                                      ↓
   *                                 拦截器处理
   *                                      ↓
   *                                 自动解包数据
   *                                      ↓
   *                                 返回实际数据
   * ```
   *
   * @template T 返回数据的类型
   * @param url 请求 URL
   * @param data 请求体数据
   * @param config 请求配置（可选）
   * @returns Promise，resolve 时返回实际数据（已解包）
   *
   * @example
   * ```typescript
   * // 创建资源
   * const material = await httpAdapter.post<Material>(
   *   '/api/v1/materials',
   *   {
   *     material_code: 'M001',
   *     material_name: '原材料A',
   *     material_type: 'RAW',
   *     base_unit: 'KG'
   *   }
   * )
   *
   * // 文件上传
   * const formData = new FormData()
   * formData.append('file', file)
   * const result = await httpAdapter.post('/api/v1/materials/import', formData)
   * ```
   */
  post<T = any>(url: string, data?: any, config?: RequestConfig): Promise<T> {
    return requestClient.post<T>(url, data, config);
  },

  /**
   * PUT 请求
   *
   * @description
   * 发送 PUT 请求更新资源。
   *
   * **数据流向**：
   * ```
   * API 方法调用 → httpAdapter.put → requestClient.put → 后端 API
   *                                     ↓
   *                                拦截器处理
   *                                     ↓
   *                                自动解包数据
   *                                     ↓
   *                                返回实际数据
   * ```
   *
   * @template T 返回数据的类型
   * @param url 请求 URL
   * @param data 请求体数据
   * @param config 请求配置（可选）
   * @returns Promise，resolve 时返回实际数据（已解包）
   *
   * @example
   * ```typescript
   * // 更新资源
   * const material = await httpAdapter.put<Material>(
   *   '/api/v1/materials/1',
   *   {
   *     material_name: '原材料A（修改）',
   *     min_stock: 200
   *   }
   * )
   * ```
   */
  put<T = any>(url: string, data?: any, config?: RequestConfig): Promise<T> {
    return requestClient.put<T>(url, data, config);
  },

  /**
   * PATCH 请求
   *
   * @description
   * 发送 PATCH 请求进行部分更新，兼容物流模块的适配器合同。
   */
  patch<T = any>(url: string, data?: any, config?: RequestConfig): Promise<T> {
    return requestClient.request<T>(url, buildPatchRequestConfig(data, config));
  },

  /**
   * DELETE 请求
   *
   * @description
   * 发送 DELETE 请求删除资源。
   *
   * **数据流向**：
   * ```
   * API 方法调用 → httpAdapter.delete → requestClient.delete → 后端 API
   *                                        ↓
   *                                   拦截器处理
   *                                        ↓
   *                                   自动解包数据
   *                                        ↓
   *                                   返回结果（通常为空）
   * ```
   *
   * @template T 返回数据的类型（通常为 void）
   * @param url 请求 URL
   * @param config 请求配置（可选）
   * @returns Promise，resolve 时返回结果
   *
   * @example
   * ```typescript
   * // 删除资源
   * await httpAdapter.delete('/api/v1/materials/1')
   * ```
   */
  delete<T = any>(url: string, config?: RequestConfig): Promise<T> {
    return requestClient.delete<T>(url, config);
  },
};

/**
 * 批量导入 HTTP 适配器
 *
 * @description
 * 与 httpAdapter 行为一致，但走 batchClient（2 分钟超时）。
 *
 * **为什么需要它**：
 * 数据导入是后端长事务操作，走默认 15 秒超时会前端报错、后端仍在执行，
 * 用户重试会造成并发导入堆积（2026-09-18 导入链路治理的重点隐患）。
 * shared 库的 API 工厂函数接受客户端注入，导入入口应改用本适配器创建的实例。
 */
export const batchHttpAdapter: LogisticsCompatibleRequestClient = {
  get<T = any>(url: string, config?: RequestConfig): Promise<T> {
    return batchClient.get<T>(url, withUrlTimeout(url, config));
  },

  post<T = any>(url: string, data?: any, config?: RequestConfig): Promise<T> {
    return batchClient.post<T>(url, data, config);
  },

  put<T = any>(url: string, data?: any, config?: RequestConfig): Promise<T> {
    return batchClient.put<T>(url, data, config);
  },

  patch<T = any>(url: string, data?: any, config?: RequestConfig): Promise<T> {
    return batchClient.request<T>(url, buildPatchRequestConfig(data, config));
  },

  delete<T = any>(url: string, config?: RequestConfig): Promise<T> {
    return batchClient.delete<T>(url, config);
  },
};
